import { useEffect, useState } from 'react';
import { Agents, Probes, Services as ServicesApi } from '../lib/api';
import type { AgentSummary, HttpProbeConfig, ServiceWatchConfig } from '@freebuff/shared';
import { Pill, Spinner, EmptyState } from '../components/Common';
import clsx from '../lib/clsx';

export function Sites() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [probes, setProbes] = useState<HttpProbeConfig[]>([]);
  const [services, setServices] = useState<ServiceWatchConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [probesR, servicesR] = await Promise.all([Probes.list(), ServicesApi.list()]);
    setProbes(probesR.probes);
    setServices(servicesR.services);
  };

  useEffect(() => {
    Agents.list().then((r) => setAgents(r.agents)).catch(() => null);
    refresh().finally(() => setLoading(false));
  }, []);

  const [probeForm, setProbeForm] = useState({ agentId: '', url: '', label: '', intervalSec: 30 });
  const [serviceForm, setServiceForm] = useState({ agentId: '', name: '' });

  const createProbe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!probeForm.agentId || !probeForm.url || !probeForm.label) return;
    await Probes.create({ ...probeForm, enabled: true });
    setProbeForm({ agentId: probeForm.agentId, url: '', label: '', intervalSec: 30 });
    await refresh();
  };
  const createService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceForm.agentId || !serviceForm.name) return;
    await ServicesApi.create({ ...serviceForm, enabled: true });
    setServiceForm({ agentId: serviceForm.agentId, name: '' });
    await refresh();
  };

  const toggleProbe = async (p: HttpProbeConfig) => {
    await Probes.update(p.id, { enabled: !p.enabled });
    await refresh();
  };
  const removeProbe = async (p: HttpProbeConfig) => {
    if (!confirm(`Remover probe "${p.label}"?`)) return;
    await Probes.remove(p.id);
    await refresh();
  };
  const removeService = async (s: ServiceWatchConfig) => {
    if (!confirm(`Remover serviço "${s.name}"?`)) return;
    await ServicesApi.remove(s.id);
    await refresh();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-header"><h3 className="font-semibold">Nova probe HTTP / SSL</h3></div>
          <div className="card-body">
            <form onSubmit={createProbe} className="space-y-3">
              <div>
                <label className="label">Agente que executa</label>
                <select value={probeForm.agentId} onChange={(e) => setProbeForm({ ...probeForm, agentId: e.target.value })} className="input" required>
                  <option value="">Selecione…</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Rótulo</label>
                  <input value={probeForm.label} onChange={(e) => setProbeForm({ ...probeForm, label: e.target.value })} required className="input" placeholder="Homepage" />
                </div>
                <div>
                  <label className="label">Intervalo (segundos)</label>
                  <input type="number" min={5} max={3600} value={probeForm.intervalSec} onChange={(e) => setProbeForm({ ...probeForm, intervalSec: Number(e.target.value) })} className="input" />
                </div>
              </div>
              <div>
                <label className="label">URL</label>
                <input value={probeForm.url} onChange={(e) => setProbeForm({ ...probeForm, url: e.target.value })} required type="url" className="input font-mono text-sm" placeholder="https://exemplo.com" />
              </div>
              <div className="text-right">
                <button type="submit" className="btn btn-primary">Adicionar probe</button>
              </div>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="font-semibold">Novo serviço systemd</h3></div>
          <div className="card-body">
            <form onSubmit={createService} className="space-y-3">
              <div>
                <label className="label">Agente</label>
                <select value={serviceForm.agentId} onChange={(e) => setServiceForm({ ...serviceForm, agentId: e.target.value })} className="input" required>
                  <option value="">Selecione…</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Nome do serviço (systemd)</label>
                <input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required className="input font-mono" placeholder="nginx" />
                <p className="text-xs text-slate-500 mt-1">Verifique com <code>systemctl list-units --type=service</code>.</p>
              </div>
              <div className="text-right">
                <button type="submit" className="btn btn-primary">Adicionar serviço</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <section className="card">
        <div className="card-header">
          <div>
            <h3 className="font-semibold">Probes HTTP</h3>
            <p className="text-xs text-slate-400">As probes são executadas pelos agentes onde foram criadas.</p>
          </div>
        </div>
        <div className="card-body">
          {probes.length === 0 ? <EmptyState title="Nenhuma probe" /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Rótulo</th><th>URL</th><th>Agente</th><th>Intervalo</th><th>Habilitada</th><th /></tr></thead>
                <tbody>
                  {probes.map((p) => (
                    <tr key={p.id}>
                      <td className="font-semibold text-white">{p.label}</td>
                      <td className="font-mono text-xs text-slate-400 max-w-md truncate">{p.url}</td>
                      <td className="text-slate-400 text-sm">{agentName(agents, p.agentId)}</td>
                      <td className="text-slate-400 text-xs">{p.intervalSec}s</td>
                      <td>
                        <button onClick={() => toggleProbe(p)} className={clsx('pill', p.enabled ? 'pill-ok' : 'pill-muted')}>
                          {p.enabled ? 'Ativa' : 'Pausada'}
                        </button>
                      </td>
                      <td className="text-right">
                        <button onClick={() => removeProbe(p)} className="btn text-danger hover:text-red-400">Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-header"><h3 className="font-semibold">Serviços systemd</h3></div>
        <div className="card-body">
          {services.length === 0 ? <EmptyState title="Nenhum serviço" /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Nome</th><th>Agente</th><th>Habilitado</th><th /></tr></thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id}>
                      <td className="font-mono text-white">{s.name}</td>
                      <td className="text-slate-400 text-sm">{agentName(agents, s.agentId)}</td>
                      <td>
                        <Pill tone={s.enabled ? 'ok' : 'muted'}>{s.enabled ? 'Sim' : 'Não'}</Pill>
                      </td>
                      <td className="text-right">
                        <button onClick={() => removeService(s)} className="btn text-danger hover:text-red-400">Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function agentName(agents: AgentSummary[], agentId: string): string {
  return agents.find((a) => a.id === agentId)?.name ?? agentId.slice(0, 6);
}
